import { useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  useDataProvider,
  useGetMany,
  useNotify,
  useTranslate,
  type Identifier,
} from "ra-core";
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  Download,
  XCircle,
} from "lucide-react";
import type { ComponentType } from "react";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";

import { ListCardsSkeleton, ListEmptyState } from "../design/ListStates";
import { APP_LOCALE, NUMERIC_DATE_FORMAT } from "../misc/locale";
import {
  formatRetentionDate,
  getRetentionEnd,
} from "../providers/commons/operationDocuments";
import type { CrmDataProvider } from "../providers/types";
import type {
  AppRole,
  DocumentResult,
  DocumentValidationResult,
  DocumentVersion,
  DocumentWithVersions,
  Sale,
} from "../types";
import {
  ReasonDialog,
  UploadDialog,
  type NewDocumentValue,
} from "./OperationDocumentDialogs";
import {
  canSeePayableHint,
  getOperationDocumentControls,
  getPayableRequirements,
} from "./operationDocumentRules";
import {
  AdvisorInvoiceNotice,
  PayableRequirementHint,
} from "./PayableRequirementHint";

const EMPTY = "—";
const SHA_PREFIX_LENGTH = 8;

const RESULT_STYLES: Record<
  DocumentResult,
  { Icon: ComponentType<{ className?: string }>; className: string }
> = {
  pendiente: { Icon: Clock, className: "bg-muted text-muted-foreground" },
  validado: { Icon: CheckCircle2, className: "bg-[#E7F5EC] text-[#23794A]" },
  observado: { Icon: AlertTriangle, className: "bg-[#FDF6D3] text-[#7A6500]" },
  rechazado: { Icon: XCircle, className: "bg-[#FBE7E4] text-[#B3321C]" },
};

/** Result of a version: always an icon AND text, never colour alone. */
const ResultBadge = ({ value }: { value: DocumentResult }) => {
  const translate = useTranslate();
  const { Icon, className } = RESULT_STYLES[value];
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium",
        className,
      )}
    >
      <Icon className="size-3.5" aria-hidden="true" />
      {translate(`crm.operations.documents.result.${value}`)}
    </span>
  );
};

const formatDateTime = (iso: string) =>
  new Date(iso).toLocaleDateString(APP_LOCALE, NUMERIC_DATE_FORMAT);

const formatSize = (bytes: number) =>
  bytes < 1024 * 1024
    ? `${Math.max(1, Math.round(bytes / 1024))} KB`
    : `${(bytes / (1024 * 1024)).toFixed(1)} MB`;

type ReasonAction =
  | { kind: "observe" | "reject"; version: DocumentVersion }
  | { kind: "delete"; documentId: Identifier };

/**
 * Almikia Epic E5 (TASK-035): the "Documentos" card of an operation. Every
 * document with all its versions; uploads always add a version, deletes are
 * soft and hidden behind "Ver eliminados". Role gating mirrors the RLS of
 * TASK-033 for display only: the server stays authoritative and its Spanish
 * errors are surfaced as-is.
 */
export const OperationDocuments = ({
  operationId,
  role,
  isAdvisor,
  greenFile = false,
}: {
  operationId: string;
  role: AppRole;
  isAdvisor: boolean;
  /** The operation's file is green (validado + verde): one requirement of the advisor's payable. */
  greenFile?: boolean;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const queryClient = useQueryClient();
  const controls = getOperationDocumentControls(role, isAdvisor);
  const queryKey = ["operation-documents", operationId];
  const [showDeleted, setShowDeleted] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [reasonAction, setReasonAction] = useState<ReasonAction | null>(null);

  const {
    data: documents,
    isPending,
    isError,
    refetch,
  } = useQuery({
    queryKey,
    queryFn: () => dataProvider.listOperationDocuments(operationId),
  });

  const userIds = [
    ...new Set(
      (documents ?? []).flatMap((doc) =>
        doc.versions.flatMap((v) =>
          [v.uploaded_by, v.reviewed_by, v.validated_by].filter(
            (id): id is Identifier => id != null,
          ),
        ),
      ),
    ),
  ];
  const { data: sales } = useGetMany<Sale>(
    "sales",
    { ids: userIds },
    { enabled: userIds.length > 0 },
  );
  const nameOf = (id: Identifier | null | undefined) => {
    const sale = (sales ?? []).find((s) => s.id === id);
    return sale ? `${sale.first_name} ${sale.last_name}` : EMPTY;
  };

  /** Runs `action`, refreshes the list and returns the error message (or null). */
  const run = async (
    action: () => Promise<unknown>,
    doneKey: string,
  ): Promise<string | null> => {
    try {
      await action();
      await queryClient.invalidateQueries({ queryKey });
      await queryClient.invalidateQueries({
        queryKey: ["operation-audit-log", operationId],
      });
      notify(doneKey, { type: "success" });
      return null;
    } catch (e) {
      return e instanceof Error
        ? e.message
        : translate("crm.operations.documents.action_error");
    }
  };

  /** Inline actions (no dialog) report the error as a notification. */
  const runInline = async (action: () => Promise<unknown>, doneKey: string) => {
    const message = await run(action, doneKey);
    if (message) notify(message, { type: "error" });
  };

  const upload = (value: NewDocumentValue, documentId?: Identifier) =>
    run(
      () =>
        dataProvider.uploadOperationDocument({
          operationId,
          file: value.file,
          docType: value.docType,
          title: value.title || undefined,
          documentId,
        }),
      "crm.operations.documents.uploaded",
    );

  const decide = (
    version: DocumentVersion,
    result: DocumentValidationResult,
    reason?: string,
  ) =>
    run(
      () => dataProvider.validateDocumentVersion(version.id, result, reason),
      `crm.operations.documents.${
        { validado: "validate", observado: "observe", rechazado: "reject" }[
          result
        ]
      }_done`,
    );

  const submitReason = async (reason: string) => {
    if (!reasonAction) return null;
    const message =
      reasonAction.kind === "delete"
        ? await run(
            () =>
              dataProvider.softDeleteDocument(reasonAction.documentId, reason),
            "crm.operations.documents.delete_done",
          )
        : await decide(
            reasonAction.version,
            reasonAction.kind === "observe" ? "observado" : "rechazado",
            reason,
          );
    if (!message) setReasonAction(null);
    return message;
  };

  const download = (version: DocumentVersion) => {
    // Open the tab synchronously with the click so popup blockers allow it.
    const tab = window.open("", "_blank");
    if (tab) tab.opener = null;
    dataProvider
      .getOperationDocumentUrl(version)
      .then((url) => {
        if (tab) tab.location.href = url;
      })
      .catch(() => {
        tab?.close();
        notify("crm.operations.documents.download_error", { type: "error" });
      });
  };

  const visible = (documents ?? []).filter(
    (doc) => showDeleted || !doc.deleted_at,
  );
  const hasDeleted = (documents ?? []).some((doc) => doc.deleted_at);

  return (
    <Card>
      <CardContent className="space-y-3" data-testid="operation-documents">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="font-semibold">
            {translate("crm.operations.documents.title")}
          </h3>
          <div className="flex flex-wrap gap-2">
            {hasDeleted ? (
              <Button
                type="button"
                variant="outline"
                className="h-11 sm:h-9"
                aria-pressed={showDeleted}
                onClick={() => setShowDeleted((value) => !value)}
              >
                {translate(
                  `crm.operations.documents.${showDeleted ? "hide" : "show"}_deleted`,
                )}
              </Button>
            ) : null}
            {controls.canUpload ? (
              <Button
                type="button"
                className="h-11 sm:h-9"
                onClick={() => setUploading(true)}
              >
                {translate("crm.operations.documents.upload")}
              </Button>
            ) : null}
          </div>
        </div>
        {controls.showAdvisorHint ? (
          <p className="text-sm text-muted-foreground">
            {translate("crm.operations.documents.advisor_hint")}
          </p>
        ) : null}
        {isAdvisor ? <AdvisorInvoiceNotice role={role} /> : null}
        {canSeePayableHint(role, isAdvisor) && documents ? (
          <PayableRequirementHint
            requirements={getPayableRequirements(greenFile, documents)}
          />
        ) : null}

        {isPending ? (
          <ListCardsSkeleton rows={2} />
        ) : isError ? (
          <div role="alert" className="space-y-2 text-sm">
            <p>{translate("crm.operations.documents.error")}</p>
            <Button
              type="button"
              variant="outline"
              className="h-11 sm:h-9"
              onClick={() => refetch()}
            >
              {translate("crm.common.retry")}
            </Button>
          </div>
        ) : visible.length === 0 ? (
          <ListEmptyState
            className="min-h-0 py-4"
            title={translate("crm.operations.documents.empty")}
            description={translate(
              controls.canUpload
                ? "crm.operations.documents.empty_hint_write"
                : "crm.operations.documents.empty_hint",
            )}
          />
        ) : (
          <ul className="space-y-4">
            {visible.map((doc) => (
              <DocumentItem
                key={doc.id}
                doc={doc}
                showDeleted={showDeleted}
                controls={controls}
                nameOf={nameOf}
                onDownload={download}
                onUploadVersion={(file) =>
                  runInline(
                    () =>
                      dataProvider.uploadOperationDocument({
                        operationId,
                        file,
                        docType: doc.doc_type,
                        documentId: doc.id,
                      }),
                    "crm.operations.documents.uploaded",
                  )
                }
                onReview={(version) =>
                  runInline(
                    () => dataProvider.reviewDocumentVersion(version.id),
                    "crm.operations.documents.review_done",
                  )
                }
                onValidate={async (version) => {
                  const message = await decide(version, "validado");
                  if (message) notify(message, { type: "error" });
                }}
                onReason={setReasonAction}
              />
            ))}
          </ul>
        )}
      </CardContent>

      {uploading ? (
        <UploadDialog
          onClose={() => setUploading(false)}
          onSubmit={async (value) => {
            const message = await upload(value);
            if (!message) setUploading(false);
            return message;
          }}
        />
      ) : null}
      {reasonAction ? (
        <ReasonDialog
          title={translate(
            `crm.operations.documents.${reasonAction.kind}_title`,
          )}
          description={
            reasonAction.kind === "delete"
              ? translate("crm.operations.documents.delete_hint")
              : undefined
          }
          onClose={() => setReasonAction(null)}
          onSubmit={submitReason}
        />
      ) : null}
    </Card>
  );
};

type ItemProps = {
  doc: DocumentWithVersions;
  showDeleted: boolean;
  controls: ReturnType<typeof getOperationDocumentControls>;
  nameOf: (id: Identifier | null | undefined) => string;
  onDownload: (version: DocumentVersion) => void;
  onUploadVersion: (file: File) => void;
  onReview: (version: DocumentVersion) => void;
  onValidate: (version: DocumentVersion) => void;
  onReason: (action: ReasonAction) => void;
};

const DocumentItem = ({
  doc,
  showDeleted,
  controls,
  nameOf,
  onDownload,
  onUploadVersion,
  onReview,
  onValidate,
  onReason,
}: ItemProps) => {
  const translate = useTranslate();
  const inputRef = useRef<HTMLInputElement>(null);
  const isDeleted = Boolean(doc.deleted_at);
  const retentionEnd = getRetentionEnd(doc);
  const versions = doc.versions.filter(
    (version) => showDeleted || !version.deleted_at,
  );

  return (
    <li className="space-y-2 rounded-lg bg-muted/40 p-3" data-testid="document">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-medium">
            {translate(`crm.operations.documents.types.${doc.doc_type}`)}
          </span>
          {doc.title ? (
            <span className="text-sm text-muted-foreground">{doc.title}</span>
          ) : null}
          {isDeleted ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
              <XCircle className="size-3.5" aria-hidden="true" />
              {translate("crm.operations.documents.deleted")}
            </span>
          ) : null}
        </div>
        {isDeleted ? null : (
          <div className="flex flex-wrap gap-2">
            {controls.canUpload ? (
              <>
                <input
                  ref={inputRef}
                  type="file"
                  className="hidden"
                  data-testid="new-version-input"
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    event.target.value = "";
                    if (file) onUploadVersion(file);
                  }}
                />
                <Button
                  type="button"
                  variant="outline"
                  className="h-11 sm:h-9"
                  onClick={() => inputRef.current?.click()}
                >
                  {translate("crm.operations.documents.upload_new_version")}
                </Button>
              </>
            ) : null}
            {controls.canDelete ? (
              <Button
                type="button"
                variant="outline"
                className="h-11 sm:h-9"
                title={
                  retentionEnd > new Date()
                    ? translate(
                        "crm.operations.documents.delete_retention_hint",
                        { date: formatRetentionDate(retentionEnd) },
                      )
                    : undefined
                }
                onClick={() => onReason({ kind: "delete", documentId: doc.id })}
              >
                {translate("crm.operations.documents.delete")}
              </Button>
            ) : null}
          </div>
        )}
      </div>
      {isDeleted && doc.delete_reason ? (
        <p className="text-sm text-muted-foreground">
          {translate("crm.operations.documents.deleted_reason", {
            reason: doc.delete_reason,
          })}
        </p>
      ) : null}

      <ol className="space-y-3">
        {versions.map((version) => {
          const isLive = !isDeleted && !version.deleted_at;
          const canDecide =
            isLive && version.result === "pendiente" && controls.canValidate;
          return (
            <li
              key={version.id}
              className="space-y-1 text-sm"
              data-testid="document-version"
            >
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium tabular-nums">
                  {translate("crm.operations.documents.version", {
                    number: version.version_number,
                  })}
                </span>
                <ResultBadge value={version.result} />
                <span>{version.file_name}</span>
                <span className="tabular-nums text-muted-foreground">
                  {formatSize(version.size_bytes)} ·{" "}
                  <span title={version.sha256}>
                    {version.sha256.slice(0, SHA_PREFIX_LENGTH)}
                  </span>
                </span>
              </div>
              <p className="text-muted-foreground">
                {translate("crm.operations.documents.uploaded_by", {
                  name: nameOf(version.uploaded_by),
                  date: formatDateTime(version.uploaded_at),
                })}
              </p>
              {version.reviewed_at ? (
                <p className="text-muted-foreground">
                  {translate("crm.operations.documents.reviewed_by", {
                    name: nameOf(version.reviewed_by),
                    date: formatDateTime(version.reviewed_at),
                  })}
                </p>
              ) : null}
              {version.validated_at ? (
                <p className="text-muted-foreground">
                  {translate("crm.operations.documents.validated_by", {
                    name: nameOf(version.validated_by),
                    date: formatDateTime(version.validated_at),
                  })}
                  {version.result_reason ? ` · ${version.result_reason}` : ""}
                </p>
              ) : null}
              <div className="flex flex-wrap gap-2 pt-1">
                <Button
                  type="button"
                  variant="outline"
                  className="h-11 sm:h-9"
                  onClick={() => onDownload(version)}
                >
                  <Download className="size-4" aria-hidden="true" />
                  {translate("crm.operations.documents.download")}
                </Button>
                {isLive && !version.reviewed_at && controls.canReview ? (
                  <Button
                    type="button"
                    variant="outline"
                    className="h-11 sm:h-9"
                    onClick={() => onReview(version)}
                  >
                    {translate("crm.operations.documents.review")}
                  </Button>
                ) : null}
                {canDecide ? (
                  <>
                    <Button
                      type="button"
                      className="h-11 sm:h-9"
                      onClick={() => onValidate(version)}
                    >
                      {translate("crm.operations.documents.validate")}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      className="h-11 sm:h-9"
                      onClick={() => onReason({ kind: "observe", version })}
                    >
                      {translate("crm.operations.documents.observe")}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      className="h-11 sm:h-9"
                      onClick={() => onReason({ kind: "reject", version })}
                    >
                      {translate("crm.operations.documents.reject")}
                    </Button>
                  </>
                ) : null}
              </div>
            </li>
          );
        })}
      </ol>
    </li>
  );
};
